// src/oura/ui/drawerSlots.ts
//
// The drawer's header and foot belong to the shell (one <Drawer> per host,
// never remounted as the viewer moves from item to item), but what they say
// belongs to the sheet inside it, which IS remounted per item (a `key`), so
// its own state resets. The shell renders two empty nodes and the sheet
// portals into them with <DrawerHead> and <DrawerFoot>: the PageSlots pattern
// (pageSlots.ts), for a drawer.
//
// API (types only; the components are in Drawer.tsx):
//   DrawerStatus  the status line under the title: Dot, group word, clock.
//   DrawerNav     "2 of 3" and previous/next, with J/K for staff.
//   DrawerBack    "‹ {label}" in place of the counter, for a sub-view.
//   DrawerSlotContext  internal: what DrawerHead, DrawerFoot and
//                  useDrawerFocusClaim reach the shell through.

import { createContext } from "react";
import type { Tone } from "./tokens";

/** The line under a drawer's title: "● Needs us · Waiting 6 working days". */
export interface DrawerStatus {
    /** No tone, no Dot (Ask for a change, which has no state yet). */
    tone?: Tone;
    /** The word the row sat under, singular: "Needs us", "Scheduled", "Answered". */
    label: string;
    /**
     * The mover's clock: "Reply by Wed 30 Sep", "Waiting 6 working days".
     * `late` draws it in rose; `title` is its long form on hover
     * ("Was due Thu 24 Sep" behind "3 working days late").
     */
    clock?: { text: string; late?: boolean; title?: string } | null;
}

/** One item in a drawer's sequence: its id and the title the buttons name. */
export interface DrawerSeqItem {
    id: string;
    title: string;
}

/** Previous and next through the group (staff) or the queue (merchant) the drawer opened from. */
export interface DrawerNav {
    /** For screen readers only: "Needs us", "Needs you". */
    group: string;
    /** 0-based; shown as "2 of 3". */
    index: number;
    total: number;
    prev: DrawerSeqItem | null;
    next: DrawerSeqItem | null;
    /**
     * Move to `id`. `via` says how: from the arrows ("button", focus stays
     * on the arrow) or from J/K ("key", focus goes to the new title). The
     * host swaps the keyed sheet and passes the new `itemKey`.
     */
    onMove(id: string, via: "button" | "key"): void;
    /** J (next) and K (previous). Staff hosts only: single-letter keys are a WCAG 2.1.4 risk for speech input. */
    keys?: boolean;
    /** While a write runs: the arrows and J/K do nothing. */
    disabled?: boolean;
    /** The current item has left its group (a completing action): the counter hides, the arrows stay. */
    settled?: boolean;
}

/** A sub-view inside the drawer ("Pick a code", a question opened from a pack). Hides `nav`. */
export interface DrawerBack {
    /** What Back returns to; cut at 30 characters on screen, whole in the accessible name. */
    label: string;
    onBack(): void;
}

/** Internal: what the pieces inside a drawer reach its shell through. */
export interface DrawerSlotApi {
    /** Where <DrawerHead> portals to; null until the shell has mounted, or on a drawer that passes `title`. */
    head: HTMLElement | null;
    /** Where <DrawerFoot> portals to; null on a drawer that passes `foot`. */
    foot: HTMLElement | null;
    titleId: string;
    statusId: string;
    /** J/K are on (staff nav, not in a sub-view). */
    navKeys: boolean;
    /** DrawerHead's h2, so the shell can focus it and read it aloud. */
    setTitleEl: (el: HTMLElement | null) => void;
    /** DrawerHead's status line: the dialog's description, and read aloud on a move. */
    setStatusEl: (el: HTMLElement | null) => void;
    /** A sheet whose move is typing: focus this, not the title, on open and on a move. Returns the release. */
    claimFocus: (el: HTMLElement) => () => void;
    /** Say `text` through the shell's polite live region. */
    announce: (text: string) => void;
}

export const DrawerSlotContext = createContext<DrawerSlotApi | null>(null);
